import { useMemo } from "react";
import { type AddNurseCandidateCriteria, isAddNurseCandidate } from "@/components/AddNurse/addNurseCandidates";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { usePredictCostBatch } from "@/hooks/usePredictCost";
import { useRevealWage } from "@/hooks/useRevealWage";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { buildCostPrediction, type CostPredictionResult } from "@/utils/costPrediction";
import { EmploymentType, type Nurse } from "@/utils/models";
import { GapsPredictCostSource, useReportPredictCostFailure } from "../useReportPredictCostFailure";
import { ADD_CANDIDATE_COST_SORT, type GapsAddOrder, orderAddCandidates } from "./gapsAddCandidates";
import { type GapsAddContext, jobIdForAdd, rolesForAdd } from "./gapsAddContext";

/** Nobody is left out for already working that day in the scheduled view — the desktop's own choice (P3-7). */
const NOBODY_ON_SHIFT: ReadonlySet<number> = new Set<number>();

/** Every employment type, as the desktop add grid starts with its contract filter. */
const ALL_CONTRACT_TYPES: readonly string[] = Object.values(EmploymentType);

export type GapsAddCandidate = {
	nurse: Nurse;
	name: string;
	/** The backend's cost and overtime for adding this person, or `null` when wages are hidden (nothing was asked). */
	cost: CostPredictionResult | null;
};

export type GapsAddCandidates = {
	/** Everyone who could take the shift, best first. Empty while a cost ranking is still loading. */
	candidates: GapsAddCandidate[];
	/** How many could take it — known before the ranking lands. */
	poolSize: number;
	order: GapsAddOrder;
	/** Wages are visible and the ranking has not arrived yet. */
	isRanking: boolean;
	/** Wages are visible and the ranking failed: the list is alphabetical instead, and the sheet says so. */
	isRankingFailed: boolean;
	/** Nobody is listed until the roster loads. */
	isRosterLoading: boolean;
	isRosterError: boolean;
};

/**
 * Who could be added to the shift, and in what order (P3-7).
 *
 * The pool is the desktop add dialog's own predicate (`isAddNurseCandidate`) with the desktop's scheduled-view
 * choices: every contract type, active in this department, no charge-only narrowing, and nobody left out for already
 * being on a shift that day — the warning gates catch overlaps and overtime at the moment of adding. The roles and job
 * are the scope's (`rolesForAdd`, `jobIdForAdd`): a linked request's — every role the department staffs in a job
 * department or for a request naming no role or "all", else the roles it names — narrowed to its job's holders; a role
 * section's roles; for a job section every role the department staffs, narrowed to the people holding that job.
 *
 * Order matches the desktop: with wages visible, the backend ranks by `ADD_CANDIDATE_COST_SORT` (least overtime, then
 * cheapest) and each row carries its cost and overtime; with wages hidden nothing is predicted and the list is
 * alphabetical.
 */
export const useGapsAddCandidates = (context: GapsAddContext): GapsAddCandidates => {
	const { chosenDepartment } = useHospitalData();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { nurses, getNurseFormattedName, isLoading: isRosterLoading, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const canViewWage = useRevealWage();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { times } = context;

	const pool = useMemo<Nurse[]>(() => {
		const criteria: AddNurseCandidateCriteria = {
			roles: rolesForAdd(context, departmentNurseTypes, jobManagementEnabled),
			contractTypes: ALL_CONTRACT_TYPES,
			activeInDepartmentId: chosenDepartment.department_id,
			// As the desktop dialog, whose cutoff date is never set: parity first (see the handoff for the follow-up).
			deactivationCutoff: null,
			chargeOnly: false,
			nurseIdsToIgnore: undefined,
			requiredJobId: jobIdForAdd(context),
			nurseIdsOnShift: NOBODY_ON_SHIFT,
		};
		return nurses.filter((nurse) => isAddNurseCandidate(nurse, criteria));
	}, [context, departmentNurseTypes, jobManagementEnabled, chosenDepartment.department_id, nurses]);

	const startISO = times.startTime.toISO();
	const endISO = times.endTime.toISO();
	const batchParams = useMemo(() => {
		if (!canViewWage || pool.length === 0 || startISO === null || endISO === null) return null;
		const jobId = jobIdForAdd(context);
		return {
			departmentId: chosenDepartment.department_id,
			nurseIds: pool.map((nurse) => nurse.id),
			startTime: startISO,
			endTime: endISO,
			sort: ADD_CANDIDATE_COST_SORT,
			...(jobId === undefined ? {} : { jobId }),
		};
	}, [canViewWage, pool, startISO, endISO, context, chosenDepartment.department_id]);
	// The whole pool in one page, as the desktop grid asks: the order is only meaningful across everyone. The floor of one
	// only matters while there is no pool, when the query is off anyway (a page size of 0 would never advance).
	const batch = usePredictCostBatch(batchParams, Math.max(pool.length, 1));
	useReportPredictCostFailure(batch, { source: GapsPredictCostSource.AddStaffSheet, departmentId: chosenDepartment.department_id, nurseCount: pool.length });

	return useMemo<GapsAddCandidates>(() => {
		const isRankingFailed = batchParams !== null && batch.isError;
		const isRanking = batchParams !== null && !isRankingFailed && batch.isLoading;
		const nameOf = (nurse: Nurse): string => getNurseFormattedName(nurse.id);
		const rankedIds = batchParams !== null && !isRankingFailed ? batch.sortedNurseIds : null;
		const { ordered, order } = orderAddCandidates({ candidates: pool, rankedIds, nameOf });
		return {
			candidates: isRanking
				? []
				: ordered.map((nurse) => ({
						nurse,
						name: nameOf(nurse),
						cost: batchParams === null ? null : buildCostPrediction(batch.predictions[nurse.id], { isGlobalSalary: Boolean(nurse.isGlobalSalary) }),
					})),
			poolSize: pool.length,
			order,
			isRanking,
			isRankingFailed,
			isRosterLoading,
			isRosterError,
		};
	}, [batchParams, batch.isError, batch.isLoading, batch.sortedNurseIds, batch.predictions, pool, getNurseFormattedName, isRosterLoading, isRosterError]);
};
